// noise.ts — Seedable 3D simplex noise (Gustavson/Eastman) + Alea PRNG, vendored
// dependency-free. This is the same simplex algorithm Sebastian Lague uses in
// Noise.cs (F3=1/3, G3=1/6, 0.6 falloff, %12 grad index, final *32), but the
// permutation table is shuffled from the Alea stream so each planet seed yields a
// distinct, deterministic planet. noise3D returns ~[-1, 1].

export type RandomFn = () => number;
export type NoiseFunction3D = (x: number, y: number, z: number) => number;

// Alea PRNG (Johannes Baagøe): deterministic, seedable, well-distributed.
export function alea(...seeds: (string | number)[]): RandomFn {
  let s0 = 0;
  let s1 = 0;
  let s2 = 0;
  let c = 1;
  let n = 0xefc8249d;

  const mash = (data: string | number): number => {
    const str = String(data);
    for (let i = 0; i < str.length; i++) {
      n += str.charCodeAt(i);
      let h = 0.02519603282416938 * n;
      n = h >>> 0;
      h -= n;
      h *= n;
      n = h >>> 0;
      h -= n;
      n += h * 0x100000000;
    }
    return (n >>> 0) * 2.3283064365386963e-10;
  };

  s0 = mash(' ');
  s1 = mash(' ');
  s2 = mash(' ');
  for (let i = 0; i < seeds.length; i++) {
    s0 -= mash(seeds[i]);
    if (s0 < 0) s0 += 1;
    s1 -= mash(seeds[i]);
    if (s1 < 0) s1 += 1;
    s2 -= mash(seeds[i]);
    if (s2 < 0) s2 += 1;
  }

  return function aleaNext(): number {
    const t = 2091639 * s0 + c * 2.3283064365386963e-10;
    s0 = s1;
    s1 = s2;
    c = t | 0;
    s2 = t - c;
    return s2;
  };
}

const F3 = 1.0 / 3.0;
const G3 = 1.0 / 6.0;

// The 12 edge-midpoint gradient vectors (Lague's Grad3, identical ordering).
const grad3 = new Float64Array([
  1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0,
  1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1,
  0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1,
]);

const fastFloor = (x: number): number => Math.floor(x) | 0;

function buildPermutationTable(random: RandomFn): Uint8Array {
  const p = new Uint8Array(512);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 0; i < 255; i++) {
    const r = i + ~~(random() * (256 - i));
    const aux = p[i];
    p[i] = p[r];
    p[r] = aux;
  }
  for (let i = 256; i < 512; i++) p[i] = p[i - 256];
  return p;
}

export function createNoise3D(random: RandomFn = Math.random): NoiseFunction3D {
  const perm = buildPermutationTable(random);
  // Precompute per-index gradient components (perm value % 12 -> grad3 row).
  const pGx = new Float64Array(512);
  const pGy = new Float64Array(512);
  const pGz = new Float64Array(512);
  for (let i = 0; i < 512; i++) {
    const row = (perm[i] % 12) * 3;
    pGx[i] = grad3[row];
    pGy[i] = grad3[row + 1];
    pGz[i] = grad3[row + 2];
  }

  return function noise3D(x: number, y: number, z: number): number {
    let n0 = 0;
    let n1 = 0;
    let n2 = 0;
    let n3 = 0;

    const s = (x + y + z) * F3;
    const i = fastFloor(x + s);
    const j = fastFloor(y + s);
    const k = fastFloor(z + s);
    const t = (i + j + k) * G3;
    const x0 = x - (i - t);
    const y0 = y - (j - t);
    const z0 = z - (k - t);

    let i1: number;
    let j1: number;
    let k1: number;
    let i2: number;
    let j2: number;
    let k2: number;
    if (x0 >= y0) {
      if (y0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
      } else if (x0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1;
      } else {
        i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1;
      }
    } else {
      if (y0 < z0) {
        i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1;
      } else if (x0 < z0) {
        i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1;
      } else {
        i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
      }
    }

    const x1 = x0 - i1 + G3;
    const y1 = y0 - j1 + G3;
    const z1 = z0 - k1 + G3;
    const x2 = x0 - i2 + 2 * G3;
    const y2 = y0 - j2 + 2 * G3;
    const z2 = z0 - k2 + 2 * G3;
    const x3 = x0 - 1 + 3 * G3;
    const y3 = y0 - 1 + 3 * G3;
    const z3 = z0 - 1 + 3 * G3;

    const ii = i & 255;
    const jj = j & 255;
    const kk = k & 255;

    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
    if (t0 >= 0) {
      const gi0 = ii + perm[jj + perm[kk]];
      t0 *= t0;
      n0 = t0 * t0 * (pGx[gi0] * x0 + pGy[gi0] * y0 + pGz[gi0] * z0);
    }
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
    if (t1 >= 0) {
      const gi1 = ii + i1 + perm[jj + j1 + perm[kk + k1]];
      t1 *= t1;
      n1 = t1 * t1 * (pGx[gi1] * x1 + pGy[gi1] * y1 + pGz[gi1] * z1);
    }
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
    if (t2 >= 0) {
      const gi2 = ii + i2 + perm[jj + j2 + perm[kk + k2]];
      t2 *= t2;
      n2 = t2 * t2 * (pGx[gi2] * x2 + pGy[gi2] * y2 + pGz[gi2] * z2);
    }
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
    if (t3 >= 0) {
      const gi3 = ii + 1 + perm[jj + 1 + perm[kk + 1]];
      t3 *= t3;
      n3 = t3 * t3 * (pGx[gi3] * x3 + pGy[gi3] * y3 + pGz[gi3] * z3);
    }

    return 32.0 * (n0 + n1 + n2 + n3);
  };
}
